import React, { useEffect, useState } from "react";
import { caseTransformingAxios } from "@shared/v2/caseTransformingAxios";
import Button from "@shared/v2/Button";
import { ArrowLeftSolidV6, ArrowRightSolidV6, ForwardSolidV6, ListSolidV6 } from "@shared/v2/Icomoon";
import { DncFlag } from "@shared/Sms";
import TwoPartyFlag from "@shared/Sms/TwoPartyFlag";
import TextLink from "@shared/v2/TextLink";
import TextButton from "@shared/v2/TextButton";
import { Popover } from "@shared/v2/Popover";
import formatPhoneNumber from "@shared/FormatPhoneNumber";
import PhoneStatusIcon from "../PhoneStatusIcon";
import Tag from "@shared/v2/Tag";

export interface CallListParticipant {
  personUuid: string;
  personFullName?: string;
  phoneNumber?: string;
  skipped?: boolean;
  callSid?: string | null;
  [key: string]: unknown;
}

export interface DialerProps {
  dialerServiceUrl: string;
  dialerServiceToken: string;
}

export interface ParticipantProps {
  name?: string;
  phoneNumber?: string;
  dnc?: boolean;
  twoParty?: boolean;
  pdpLink?: string;
  callIcon?: React.ReactNode;
  tooltip?: string;
  isDataFetched?: boolean;
}

export interface PreviewStateProps {
  isCallInProgress?: boolean;
  shouldSaveBeforePreview?: boolean;
  isDisabled?: boolean;
  refreshKey?: string;
  currentParticipantUuid?: string | null;
}

export interface CallListActions {
  onPreview?: (personUuid: string, followingParticipant?: CallListParticipant) => void | Promise<unknown>;
  onSaveAndPreview?: (
    personUuid: string,
    followingParticipant?: CallListParticipant | null,
  ) => Promise<boolean | void> | void;
  onNextParticipantChange: (participant?: CallListParticipant | null) => void;
}

export interface CallListPreviewProps {
  dialer: DialerProps;
  participant?: ParticipantProps | null;
  previewState: PreviewStateProps;
  actions: CallListActions;
  className?: string;
  callList?: CallListParticipant[] | null;
}

export const getCallList = async (
  dialerServiceUrl: string,
  dialerServiceToken: string,
): Promise<CallListParticipant[]> => {
  try {
    const res = await caseTransformingAxios.get<CallListParticipant[]>(
      `${dialerServiceUrl}/dialer/call_list`,
      {
        params: { token: dialerServiceToken },
      },
    );
    return res.data || [];
  } catch {
    return [];
  }
};

const findNextUndialedParticipant = (
  list: CallListParticipant[],
  startIndex: number,
  excludedUuids: (string | null | undefined)[] = [],
): CallListParticipant | null => {
  for (let offset = 1; offset <= list.length; offset += 1) {
    const candidate = list[(startIndex + offset) % list.length];
    if (!candidate.callSid && !excludedUuids.includes(candidate.personUuid)) return candidate;
  }
  return null;
};

const CallListPreview = ({
  dialer,
  participant = null,
  previewState,
  actions,
  className = "",
  callList: propCallList,
}: CallListPreviewProps) => {
  const { dialerServiceUrl, dialerServiceToken } = dialer;
  const {
    name = "",
    phoneNumber = "",
    dnc = false,
    twoParty = false,
    pdpLink = "",
    callIcon = null,
    tooltip = "",
    isDataFetched = false,
  } = participant || {};
  const { isCallInProgress, shouldSaveBeforePreview, isDisabled, refreshKey, currentParticipantUuid } =
    previewState;
  const { onPreview, onSaveAndPreview, onNextParticipantChange } = actions;
  const [internalCallList, setInternalCallList] = useState<CallListParticipant[] | null>(null);
  const callList = propCallList !== undefined ? propCallList : internalCallList;
  const previewableParticipants = (callList || []).filter((listParticipant) => !listParticipant.callSid);
  const [selectedParticipantUuid, setSelectedParticipantUuid] = useState<string | null>(() => {
    const initialList = propCallList !== undefined ? propCallList : null;
    return currentParticipantUuid || initialList?.[0]?.personUuid || null;
  });

  useEffect(() => {
    if (!callList || callList.length === 0) return;

    if (currentParticipantUuid && callList.some((p) => p.personUuid === currentParticipantUuid)) {
      setSelectedParticipantUuid(currentParticipantUuid);
    } else {
      setSelectedParticipantUuid((previousParticipantUuid) =>
        previousParticipantUuid && callList.some((p) => p.personUuid === previousParticipantUuid)
          ? previousParticipantUuid
          : callList[0].personUuid,
      );
    }
  }, [callList, currentParticipantUuid]);

  const currentParticipantIndex = selectedParticipantUuid
    ? previewableParticipants.findIndex((p) => p.personUuid === selectedParticipantUuid)
    : -1;
  const firstUndialedParticipant = previewableParticipants[0] || null;
  const selectedParticipantIndex = selectedParticipantUuid
    ? (callList || []).findIndex((p) => p.personUuid === selectedParticipantUuid)
    : -1;
  const nextParticipant =
    selectedParticipantIndex === -1
      ? firstUndialedParticipant
      : findNextUndialedParticipant(callList || [], selectedParticipantIndex, [selectedParticipantUuid]);
  const followingParticipant = nextParticipant
    ? findNextUndialedParticipant(
        callList || [],
        (callList || []).findIndex((p) => p.personUuid === nextParticipant.personUuid),
        [nextParticipant.personUuid, selectedParticipantUuid],
      )
    : null;
  const canViewNext = shouldSaveBeforePreview
    ? currentParticipantUuid
      ? Boolean(nextParticipant)
      : previewableParticipants.length > 0
    : previewableParticipants.length > 1;
  const isPreviewNavigationDisabled = isDisabled || isCallInProgress || shouldSaveBeforePreview;
  const isNextNavigationDisabled =
    isDisabled ||
    isCallInProgress ||
    !canViewNext ||
    (shouldSaveBeforePreview ? !onSaveAndPreview : !onPreview);
  const hasRemainingParticipants =
    isCallInProgress || shouldSaveBeforePreview
      ? previewableParticipants.length > 0
      : previewableParticipants.length > 1;
  const hasValidParticipant = Boolean(participant?.phoneNumber);
  const hasNextParticipant = hasValidParticipant && (callList ? hasRemainingParticipants : true);

  const previewParticipant = (participantIndex: number) => {
    const currentParticipant = previewableParticipants[participantIndex];
    const followingParticipant =
      previewableParticipants[(participantIndex + 1) % previewableParticipants.length];

    if (!currentParticipant || isPreviewNavigationDisabled || !onPreview) return;

    setSelectedParticipantUuid(currentParticipant.personUuid);
    void onPreview(currentParticipant.personUuid, followingParticipant);
  };

  const viewNext = () => {
    if (!canViewNext || isCallInProgress || isDisabled) return;
    window.clarity?.("event", "conferenceDialerViewNext");

    const nextParticipantIndex =
      currentParticipantIndex === -1 ? 0 : (currentParticipantIndex + 1) % previewableParticipants.length;

    if (shouldSaveBeforePreview) {
      const saveAndPreviewParticipant = nextParticipant || previewableParticipants[nextParticipantIndex];
      const saveAndPreviewFollowingParticipant = currentParticipantUuid
        ? followingParticipant
        : previewableParticipants[(nextParticipantIndex + 1) % previewableParticipants.length];
      if (!onSaveAndPreview || !saveAndPreviewParticipant) return;

      void Promise.resolve(
        onSaveAndPreview(saveAndPreviewParticipant.personUuid, saveAndPreviewFollowingParticipant),
      ).then((wasPreviewed) => {
        if (wasPreviewed) setSelectedParticipantUuid(saveAndPreviewParticipant.personUuid);
      });
      return;
    }

    previewParticipant(nextParticipantIndex);
  };

  useEffect(() => {
    if (propCallList === undefined) {
      void getCallList(dialerServiceUrl, dialerServiceToken).then(setInternalCallList);
    }
  }, [dialerServiceToken, dialerServiceUrl, refreshKey, propCallList]);

  useEffect(() => {
    if (!callList) return;

    onNextParticipantChange(nextParticipant);
  }, [callList, nextParticipant, onNextParticipantChange, previewableParticipants.length]);

  return (
    <div
      className={`tw-whitespace-nowrap tw-border-[1px] tw-border-solid tw-rounded tw-border-gray-10 tw-w-full tw-p-[8px] tw-flex tw-justify-between tw-text-gray-75 ${className}`}
    >
      <div className="tw-flex tw-items-center tw-gap-[8px]">
        <Popover placement="top-start">
          {/* @ts-expect-error props */}
          <Popover.Trigger>
            <div className="tw-flex">
              {/* @ts-expect-error props */}
              <TextButton
                className="tw-inline-flex tw-items-center tw-gap-[4px]"
                onClick={() => {
                  window.clarity?.("event", "conferenceDialerCallList");
                }}
              >
                <ListSolidV6 size="s" />
                Call List
              </TextButton>
            </div>
          </Popover.Trigger>
          {/* @ts-expect-error props */}
          <Popover.Content className="tw-rounded-[8px]">
            <div className="tw-rounded-t-[8px] tw-px-[12px] tw-py-[8px] tw-flex tw-justify-between tw-items-center tw-gap-[8px] tw-border-solid tw-border-[1px] tw-border-gray-10 tw-text-gray-50">
              <span className="tw-font-semibold">Call List</span>
              <span>
                {previewableParticipants.length}/{callList?.length ?? 0} Remaining
              </span>
            </div>
            {previewableParticipants.map((callee) => (
              <Popover.Item
                key={callee.personUuid}
                className="tw-whitespace-nowrap"
                disabled={isPreviewNavigationDisabled}
                // @ts-expect-error onClick inferred as undefined from JS default parameter
                onClick={() => previewParticipant(previewableParticipants.indexOf(callee))}
              >
                <div className="tw-w-full tw-flex tw-justify-between tw-items-center tw-gap-[8px]">
                  <span className="tw-font-semibold">{callee.personFullName || "No Name"}</span>
                  {callee.skipped && (
                    <Tag className="!tw-text-gray-50 tw-ml-auto" removable={false}>
                      Skipped
                    </Tag>
                  )}
                  <span>{formatPhoneNumber(callee.phoneNumber)}</span>
                </div>
              </Popover.Item>
            ))}
          </Popover.Content>
        </Popover>
        {hasNextParticipant && (
          <>
            <ArrowRightSolidV6 size="m" />
            <span>Up Next:</span>
            {/* @ts-expect-error props */}
            <TextLink href={pdpLink} target="_blank">
              <div className="tw-text-14d tw-font-semibold tw-max-w-[214px] tw-whitespace-nowrap tw-overflow-hidden tw-text-ellipsis">
                {name}
              </div>
            </TextLink>
            <div className="tw-font-semibold tw-text-gray-50">Mobile: {phoneNumber}</div>
            <DncFlag show={dnc} />
            <TwoPartyFlag show={twoParty} />
            {/* @ts-expect-error props */}
            {isDataFetched && <PhoneStatusIcon callIcon={callIcon} tooltip={tooltip} />}
          </>
        )}
      </div>
      <div className="tw-flex tw-gap-[8px]">
        {/* @ts-expect-error props */}
        <Button
          schema="tertiary"
          className="tw-inline-flex tw-items-center tw-gap-[4px]"
          disabled={!onPreview || isPreviewNavigationDisabled || currentParticipantIndex <= 0}
          onClick={() => previewParticipant(currentParticipantIndex - 1)}
        >
          <ArrowLeftSolidV6 size="s" />
          Back
        </Button>
        {/* @ts-expect-error props */}
        <Button
          schema="tertiary"
          className="tw-inline-flex tw-items-center tw-gap-[4px]"
          disabled={isNextNavigationDisabled}
          onClick={viewNext}
        >
          {shouldSaveBeforePreview && canViewNext ? "Save + View Next" : "View Next"}
          <ForwardSolidV6 size="s" />
        </Button>
      </div>
    </div>
  );
};

export default CallListPreview;
